import React, { useEffect, useState } from "react";
import { Smile, Save, Search, X, History as HistoryIcon } from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader, SectionCard } from "../components/Common";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

// Universal Numbering System (US) — tooth 1-16 upper, 17-32 lower
// Upper right (patient's right, viewer's left) 1..8, upper left 9..16
// Lower left 17..24, lower right 25..32
const UPPER_ROW = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const LOWER_ROW = [32, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17];

const CONDITIONS = [
  { key: "healthy",    label: "Healthy",       color: "bg-emerald-100 text-emerald-800 border-emerald-300", dot: "bg-emerald-500" },
  { key: "caries",     label: "Caries",        color: "bg-amber-100 text-amber-900 border-amber-300",       dot: "bg-amber-500" },
  { key: "filling",    label: "Filling",       color: "bg-sky-100 text-sky-800 border-sky-300",             dot: "bg-sky-500" },
  { key: "crown",      label: "Crown",         color: "bg-indigo-100 text-indigo-800 border-indigo-300",    dot: "bg-indigo-500" },
  { key: "root_canal", label: "Root canal",    color: "bg-purple-100 text-purple-800 border-purple-300",    dot: "bg-purple-500" },
  { key: "sealant",    label: "Sealant",       color: "bg-teal-100 text-teal-800 border-teal-300",          dot: "bg-teal-500" },
  { key: "fracture",   label: "Fracture",      color: "bg-orange-100 text-orange-800 border-orange-300",    dot: "bg-orange-500" },
  { key: "extracted",  label: "Extracted",     color: "bg-rose-100 text-rose-800 border-rose-300",          dot: "bg-rose-500" },
  { key: "missing",    label: "Missing",       color: "bg-slate-200 text-slate-600 border-slate-300",       dot: "bg-slate-400" },
];
const CONDITION_MAP = Object.fromEntries(CONDITIONS.map((c) => [c.key, c]));

function Tooth({ num, mark, selected, onClick }) {
  const cond = mark?.condition;
  const style = cond && CONDITION_MAP[cond] ? CONDITION_MAP[cond].color : "bg-white text-slate-700 border-slate-300";
  const ring = selected ? "ring-2 ring-offset-1 ring-[#1976D2]" : "";
  return (
    <button
      data-testid={`tooth-${num}`}
      onClick={onClick}
      className={`w-9 h-11 rounded-md border-2 text-[11px] font-semibold flex flex-col items-center justify-center hover:brightness-95 transition ${style} ${ring}`}
      title={cond ? `#${num} · ${CONDITION_MAP[cond].label}` : `#${num}`}
    >
      <div>{num}</div>
      {cond && <div className="w-1.5 h-1.5 rounded-full mt-0.5 bg-current opacity-70" />}
    </button>
  );
}

function TA({ label, value, onChange, testId, rows = 3 }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      <textarea
        data-testid={testId}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-2 py-2 rounded-md border border-slate-200 text-sm"
      />
    </label>
  );
}

export default function Dentist() {
  const { organization } = useAuth();
  const enabled = organization?.features?.dentist;
  const [patient, setPatient] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [chart, setChart] = useState({}); // {toothNum: {condition, note}}
  const [selected, setSelected] = useState(null); // tooth num currently being edited
  const [chief, setChief] = useState("");
  const [findings, setFindings] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [proceduresDone, setProceduresDone] = useState("");
  const [treatmentPlan, setTreatmentPlan] = useState("");
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  if (!enabled) return <Navigate to="/app" replace />;

  const search = async (v) => {
    setQ(v);
    if (v.length < 2) { setResults([]); return; }
    try { const { data } = await api.get(`/patients?q=${encodeURIComponent(v)}&limit=8`); setResults(data || []); }
    catch { setResults([]); }
  };

  const loadHistory = async (pid) => {
    try { const { data } = await api.get(`/speciality/dental/exams?patient_id=${pid}`); setHistory(data || []); }
    catch { setHistory([]); }
  };
  const loadLatestChart = async (pid) => {
    try {
      const { data } = await api.get(`/speciality/dental/latest/${pid}`);
      const seed = {};
      (data?.tooth_chart || []).forEach((m) => { seed[m.tooth] = { condition: m.condition, note: m.note || "" }; });
      setChart(seed);
    } catch { setChart({}); }
  };

  const pick = (p) => { setPatient(p); setResults([]); setQ(""); loadHistory(p.patient_id); loadLatestChart(p.patient_id); };
  const clearPatient = () => { setPatient(null); setHistory([]); setChart({}); setSelected(null); };

  const setToothCondition = (cond) => {
    if (selected == null) return;
    setChart((c) => ({ ...c, [selected]: { ...(c[selected] || {}), condition: cond } }));
  };
  const setToothNote = (note) => {
    if (selected == null) return;
    setChart((c) => ({ ...c, [selected]: { ...(c[selected] || {}), condition: c[selected]?.condition || "healthy", note } }));
  };
  const clearTooth = () => {
    if (selected == null) return;
    setChart((c) => { const next = { ...c }; delete next[selected]; return next; });
  };

  const resetForm = () => { setChief(""); setFindings(""); setDiagnosis(""); setProceduresDone(""); setTreatmentPlan(""); setNotes(""); };

  const save = async () => {
    if (!patient) return toast.error("Pick a patient first");
    const tooth_chart = Object.entries(chart)
      .filter(([, v]) => v && v.condition)
      .map(([tooth, v]) => ({ tooth: Number(tooth), condition: v.condition, note: v.note || "" }));
    setSaving(true);
    try {
      await api.post("/speciality/dental/exams", {
        patient_id: patient.patient_id,
        tooth_chart,
        chief_complaint: chief, findings, diagnosis,
        procedures_done: proceduresDone, treatment_plan: treatmentPlan, notes,
      });
      toast.success("Dental exam saved");
      resetForm();
      loadHistory(patient.patient_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const markedCount = Object.keys(chart).length;
  const selectedMark = selected != null ? chart[selected] : null;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dentist-page">
      <PageHeader
        title="Dental Clinic"
        subtitle={`Tooth chart · Procedures · Treatment plan · ${organization?.name || ""}`}
      />

      {!patient ? (
        <SectionCard title="Select a patient" testId="dnt-patient-picker">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="dnt-patient-search"
              value={q}
              onChange={(e) => search(e.target.value)}
              placeholder="Search by name / Patient ID / phone"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm"
            />
          </div>
          {results.length > 0 && (
            <div className="mt-2 rounded-lg border border-slate-200 divide-y max-w-md bg-white">
              {results.map((p) => (
                <button
                  key={p.patient_id}
                  onClick={() => pick(p)}
                  data-testid={`dnt-pick-${p.patient_id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between"
                >
                  <span className="text-sm font-medium">{p.first_name} {p.last_name}</span>
                  <span className="text-xs text-slate-500 tabular">{p.ths_id}</span>
                </button>
              ))}
            </div>
          )}
        </SectionCard>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid="dnt-patient-banner">
          <div>
            <div className="font-semibold text-slate-900">{patient.first_name} {patient.last_name}</div>
            <div className="text-xs text-slate-500 tabular">{patient.ths_id} · {patient.phone || "no phone"} · {patient.gender || "—"}</div>
          </div>
          <button onClick={clearPatient} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><X className="w-3 h-3" /> Change patient</button>
        </div>
      )}

      {patient && (
        <>
          <SectionCard
            title={<span className="flex items-center gap-2"><Smile className="w-4 h-4" /> Tooth chart · Universal 1–32 · {markedCount} marked</span>}
            testId="dnt-chart"
          >
            <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Upper (maxillary)</div>
            <div className="flex justify-center gap-1 flex-wrap">
              {UPPER_ROW.map((n, i) => (
                <React.Fragment key={n}>
                  {i === 8 && <div className="w-3" />}
                  <Tooth num={n} mark={chart[n]} selected={selected === n} onClick={() => setSelected(n)} />
                </React.Fragment>
              ))}
            </div>
            <div className="h-2" />
            <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Lower (mandibular)</div>
            <div className="flex justify-center gap-1 flex-wrap">
              {LOWER_ROW.map((n, i) => (
                <React.Fragment key={n}>
                  {i === 8 && <div className="w-3" />}
                  <Tooth num={n} mark={chart[n]} selected={selected === n} onClick={() => setSelected(n)} />
                </React.Fragment>
              ))}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-2">
              {CONDITIONS.map((c) => (
                <div key={c.key} className={`text-[11px] px-2 py-1 rounded-md border ${c.color} flex items-center gap-1.5`}>
                  <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                  {c.label}
                </div>
              ))}
            </div>

            {/* Selected tooth editor */}
            {selected != null && (
              <div className="mt-5 rounded-xl border-2 border-[#1976D2] bg-blue-50 p-4" data-testid="dnt-tooth-editor">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold text-slate-900">Tooth #{selected} · Universal</div>
                  <button onClick={() => setSelected(null)} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"><X className="w-3 h-3" /> Close</button>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {CONDITIONS.map((c) => (
                    <button
                      key={c.key}
                      data-testid={`tooth-cond-${c.key}`}
                      onClick={() => setToothCondition(c.key)}
                      className={`px-3 h-8 rounded-md border text-xs font-medium ${selectedMark?.condition === c.key ? `${c.color} ring-2 ring-offset-1 ring-slate-700` : `${c.color} opacity-70 hover:opacity-100`}`}
                    >
                      {c.label}
                    </button>
                  ))}
                  <button onClick={clearTooth} className="px-3 h-8 rounded-md border border-slate-300 bg-white text-xs font-medium text-slate-600 hover:bg-slate-50">Clear</button>
                </div>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Note (optional)</span>
                  <input
                    data-testid="tooth-note"
                    value={selectedMark?.note || ""}
                    onChange={(e) => setToothNote(e.target.value)}
                    placeholder="e.g. distal surface caries, pain on cold"
                    className="mt-1 w-full h-9 px-2 rounded-md border border-slate-200 text-sm"
                  />
                </label>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Clinical notes" testId="dnt-notes">
            <div className="grid md:grid-cols-2 gap-3">
              <TA label="Chief complaint" value={chief} onChange={setChief} testId="dnt-chief" />
              <TA label="Findings" value={findings} onChange={setFindings} testId="dnt-findings" />
              <TA label="Diagnosis" value={diagnosis} onChange={setDiagnosis} testId="dnt-dx" />
              <TA label="Procedures done today" value={proceduresDone} onChange={setProceduresDone} testId="dnt-procs" />
              <TA label="Treatment plan (next visits)" value={treatmentPlan} onChange={setTreatmentPlan} testId="dnt-plan" />
              <TA label="Other notes" value={notes} onChange={setNotes} testId="dnt-notes-text" />
            </div>
            <div className="mt-4 flex justify-end">
              <button
                data-testid="dnt-save"
                onClick={save}
                disabled={saving}
                className="px-5 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] disabled:opacity-60 text-white text-sm font-semibold flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save exam"}
              </button>
            </div>
          </SectionCard>

          <SectionCard
            title={<span className="flex items-center gap-2"><HistoryIcon className="w-4 h-4" /> Previous exams · {history.length}</span>}
            testId="dnt-history"
          >
            {history.length === 0 ? (
              <div className="text-sm text-slate-500 p-6 text-center">No prior dental exams recorded for this patient.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2">Date</th>
                      <th className="text-left">Diagnosis</th>
                      <th className="text-left">Marked teeth</th>
                      <th className="text-left">Procedures</th>
                      <th className="text-left">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {history.map((h) => (
                      <tr key={h.exam_id} className="hover:bg-slate-50" data-testid={`dnt-row-${h.exam_id}`}>
                        <td className="py-2 tabular text-slate-600">{new Date(h.created_at).toLocaleDateString()}</td>
                        <td className="max-w-[220px] truncate">{h.diagnosis || "—"}</td>
                        <td className="text-xs">{(h.tooth_chart || []).length} tooth marks</td>
                        <td className="max-w-[240px] truncate text-xs text-slate-600">{h.procedures_done || "—"}</td>
                        <td className="text-xs text-slate-500">{h.recorded_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
